import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Section from './Section';
import stepOneIcon from 'src/assets/images/landing/step-one-icon.png';
import stepTwoIcon from 'src/assets/images/landing/step-two-icon.png';
import stepThreeIcon from 'src/assets/images/landing/step-three-icon.png';
import step1Gif from 'src/assets/images/landing/step-1-inspire.gif';
import step2Gif from 'src/assets/images/landing/step-2-empower.gif';
import step3Gif from 'src/assets/images/landing/step-3-support.gif';
import stickerImg from 'src/assets/images/landing/sticker-how-it-works.png';

const STEPS = [
  {
    icon: stepOneIcon,
    gif: step1Gif,
    title: 'Inspire Writing',
    description:
      'Students write across a range of subjects and interests and produce their own digital essays or pages, or printable zines.',
    accent: '#FFE082',
  },
  {
    icon: stepTwoIcon,
    gif: step2Gif,
    title: 'Empower with Scaffolds',
    description:
      'Boost confidence by supporting skillful writing. Scaffolds include planning blocks, signal words, important words, and real-time feedback that names strategies to help improve writing.',
    accent: '#A5D6A7',
  },
  {
    icon: stepThreeIcon,
    gif: step3Gif,
    title: 'Support Student Progress',
    description:
      'With a comprehensive view of the whole class, easily pinpoint the progress of each student. Then, dive into the specifics of any individual\'s work and leave comments to instruct and encourage.',
    accent: '#90CAF9',
  },
];

export default function HowItWorksSection() {
  return (
    <Section background="#F7F7F7" id="how-it-works">
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 }, position: 'relative' }}>
        {/* Sticker decoration */}
        <Box
          component="img"
          src={stickerImg}
          alt=""
          sx={{
            position: 'absolute',
            top: { xs: -30, md: -50 },
            right: { xs: 10, md: 80 },
            width: { xs: 50, md: 80 },
            transform: 'rotate(12deg)',
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 800,
            color: '#2C2C2C',
            mb: 2,
          }}
        >
          How It Works
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lato, Inter, sans-serif',
            fontSize: { xs: '1rem', md: '1.15rem' },
            color: '#555',
            maxWidth: 550,
            mx: 'auto',
            lineHeight: 1.7,
          }}
        >
          Three simple steps to transform writing in your classroom
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 6, md: 8 } }}>
        {STEPS.map((step, index) => (
          <Box
            key={step.title}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 3, md: 6 },
              alignItems: 'center',
              direction: { md: index % 2 === 1 ? 'rtl' : 'ltr' },
            }}
          >
            {/* GIF */}
            <Box
              sx={{
                direction: 'ltr',
                order: { xs: 0, md: index % 2 === 1 ? 1 : 0 },
              }}
            >
              <Box
                component="img"
                src={step.gif}
                alt={step.title}
                sx={{
                  width: '100%',
                  display: 'block',
                  borderRadius: '4px',
                }}
              />
            </Box>

            {/* Text */}
            <Box sx={{ direction: 'ltr' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  component="img"
                  src={step.icon}
                  alt=""
                  sx={{ width: 40, height: 40 }}
                />
                <Box
                  sx={{
                    display: 'inline-block',
                    backgroundColor: step.accent,
                    border: '2px solid #1e1e1e',
                    borderRadius: '4px',
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 800,
                      fontSize: '0.75rem',
                      color: '#2C2C2C',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Step {index + 1}
                  </Typography>
                </Box>
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 800,
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  color: '#2C2C2C',
                  lineHeight: 1.15,
                  mb: 1.5,
                  textTransform: 'uppercase',
                  letterSpacing: '-0.01em',
                }}
              >
                {step.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  color: '#555',
                  lineHeight: 1.8,
                }}
              >
                {step.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}
